import { useState } from "react";
import { motion } from "framer-motion";
import { Send, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

interface CommentInputProps {
  onSubmit: (content: string) => Promise<void>;
  isSubmitting: boolean;
  isFixed?: boolean;
  replyingTo?: { id: string; username: string } | null;
  onCancelReply?: () => void;
}

export function CommentInput({ 
  onSubmit, 
  isSubmitting, 
  isFixed = false,
  replyingTo,
  onCancelReply
}: CommentInputProps) {
  const { user } = useAuth();
  const [content, setContent] = useState("");
  const isMobile = useIsMobile();

  const handleSubmit = async () => {
    if (!content.trim() || isSubmitting) return;
    await onSubmit(content.trim());
    setContent("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  if (!user) {
    return (
      <div className="text-center py-4 text-white/50 text-sm">
        Sign in to join the conversation
      </div>
    );
  }

  const inputContent = (
    <div className="space-y-2">
      {/* Replying indicator */}
      {replyingTo && (
        <div className="flex items-center justify-between px-3 py-2 bg-brand-cyan/10 rounded-lg">
          <span className="text-xs text-brand-cyan">
            Replying to @{replyingTo.username}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancelReply}
            className="h-5 w-5 p-0 text-white/50 hover:text-white hover:bg-transparent"
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}
      
      <div className="flex items-end gap-3">
        <div className="flex-1 relative">
          <Textarea
            placeholder={replyingTo ? `Reply to @${replyingTo.username}...` : "Write a comment…"}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
            className={cn(
              "resize-none min-h-[44px] max-h-[120px] pr-4",
              "bg-black/30 backdrop-blur-sm rounded-xl",
              "border border-brand-cyan/20",
              "text-white/90 placeholder:text-white/40",
              "focus:border-brand-cyan/50 focus:ring-0",
              "focus:shadow-[0_0_15px_rgba(0,194,255,0.2)]",
              "transition-all duration-300"
            )}
          />
        </div>
        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Button
            onClick={handleSubmit}
            disabled={!content.trim() || isSubmitting}
            size="icon"
            className={cn(
              "h-11 w-11 rounded-full",
              "bg-gradient-to-r from-brand-purple via-brand-pink to-brand-cyan",
              "hover:shadow-[0_0_20px_rgba(112,0,255,0.5)]",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              "transition-all duration-300",
              "animate-gradient-pulse bg-[length:200%_200%]"
            )}
          >
            <Send className="h-4 w-4 text-white" />
          </Button>
        </motion.div>
      </div>
    </div>
  );

  // Fixed mobile input at bottom
  if (isFixed && isMobile) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-lg border-t border-white/10 p-4 pb-safe">
        {inputContent}
      </div>
    );
  }

  return (
    <div className="p-4 border-b border-brand-cyan/10">
      {inputContent}
    </div>
  );
}
